import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import Toast from '../components/ui/Toast';
import { globalToast } from '../services/toast/globalToast';

const ToastContext = createContext(null);

let toastIdCounter = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((type, title, message, duration = 5000) => {
    const id = ++toastIdCounter;
    setToasts((prev) => [...prev, { id, type, title, message, duration }]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const toast = useCallback(
    (options) => {
      if (typeof options === 'string') {
        return addToast('info', '', options, 5000);
      }
      const { type = 'info', title = '', message = '', duration = 5000 } = options;
      return addToast(type, title, message, duration);
    },
    [addToast]
  );

  const success = useCallback(
    (message, title = '') => addToast('success', title, message, 5000),
    [addToast]
  );

  const error = useCallback(
    (message, title = '') => addToast('error', title, message, 5000),
    [addToast]
  );

  const warning = useCallback(
    (message, title = '') => addToast('warning', title, message, 5000),
    [addToast]
  );

  const info = useCallback(
    (message, title = '') => addToast('info', title, message, 5000),
    [addToast]
  );

  const toastApi = {
    toast,
    success,
    error,
    warning,
    info,
  };

  useEffect(() => {
    globalToast.register(toastApi);
    return () => globalToast.unregister();
  }, [toastApi]);

  return (
    <ToastContext.Provider value={toastApi}>
      {children}
      {/* Toast Container */}
      <div className="fixed inset-0 pointer-events-none z-[9999] flex flex-col items-end gap-3 p-4 sm:p-6">
        <div className="flex flex-col gap-3 w-full max-w-sm">
          {toasts.map((t) => (
            <Toast
              key={t.id}
              id={t.id}
              type={t.type}
              title={t.title}
              message={t.message}
              duration={t.duration}
              onClose={removeToast}
            />
          ))}
        </div>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

export default ToastContext;
